import React, { useState } from "react";
import * as engineService from "@/shared/api/services/engineService";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Badge } from "@/shared/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import {
  Plus,
  Search,
  MoreHorizontal,
  Pencil,
  Eye,
  Power,
  PowerOff,
  Users,
  CheckCircle2,
  XCircle,
  Clock,
  Building2,
} from "lucide-react";
import EngineLayout from "@/shared/components/engine/EngineLayout";
import EngineResellerForm from "@/shared/components/engine/EngineResellerForm";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

const statusConfig = {
  ACTIVE: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700", label: "Active" },
  active: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700", label: "Active" },
  SUSPENDED: { icon: XCircle, color: "bg-red-100 text-red-700", label: "Suspended" },
  suspended: { icon: XCircle, color: "bg-red-100 text-red-700", label: "Suspended" },
  INACTIVE: { icon: Clock, color: "bg-amber-100 text-amber-700", label: "Inactive" },
  pending: { icon: Clock, color: "bg-amber-100 text-amber-700", label: "Pending" },
};

export default function EngineResellers() {
  const [search, setSearch] = useState("");
  const [statusDialog, setStatusDialog] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingReseller, setEditingReseller] = useState(null);
  const [saving, setSaving] = useState(false);
  const queryClient = useQueryClient();
  const { t } = useLanguage();
  const resellerName = "Gaming Partners Ltd";

  const { data: resellers = [], isLoading } = useQuery({
    queryKey: ["engine-resellers"],
    queryFn: () => engineService.getMyResellers(),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => engineService.updateReseller(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["engine-resellers"]);
      setStatusDialog(null);
    },
  });

  const createMutation = useMutation({
    mutationFn: (data) => engineService.createReseller(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["engine-resellers"]);
      setShowForm(false);
      setEditingReseller(null);
      setSaving(false);
    },
  });

  const handleSaveReseller = async (data) => {
    setSaving(true);
    try {
      if (editingReseller) {
        // Update reseller
        await updateMutation.mutateAsync({ 
          id: editingReseller.id, 
          data: { name: data.name, status: data.status } 
        });
      } else {
        // Create reseller - only send backend-required fields
        const createData = {
          email: data.email,
          password: data.password,
          name: data.name,
        };
        await createMutation.mutateAsync(createData);
      }
    } catch (error) {
      // Error is handled by global error handler
    } finally {
      setSaving(false);
      setShowForm(false);
      setEditingReseller(null);
    }
  };

  const handleEditReseller = (reseller) => {
    setEditingReseller(reseller);
    setShowForm(true);
  };

  const filteredResellers = resellers.filter(r =>
    r.name?.toLowerCase().includes(search.toLowerCase()) ||
    r.code?.toLowerCase().includes(search.toLowerCase())
  );

  const handleToggleStatus = async () => {
    if (!statusDialog) return;
    const currentStatus = statusDialog.status?.toUpperCase();
    const newStatus = currentStatus === "ACTIVE" ? "SUSPENDED" : "ACTIVE";
    await updateMutation.mutateAsync({ id: statusDialog.id, data: { name: statusDialog.name, status: newStatus } });
  };

  return (
    <EngineLayout currentPage="EngineResellers" resellerName={resellerName}>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{t("resellers")}</h1>
            <p className="text-slate-500">{resellers.length} {t("partnersCount")}</p>
          </div>
          <Button className="gap-2 bg-sky-600 hover:bg-sky-700" onClick={() => { setEditingReseller(null); setShowForm(true); }}>
            <Plus className="h-4 w-4" />
            {t("addReseller")}
          </Button>
        </div>

        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder={t("search")} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t("reseller")}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase hidden sm:table-cell">{t("code")}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t("status")}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase hidden md:table-cell">{t("merchants")}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase hidden lg:table-cell">{t("created")}</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {isLoading ? (
                  <tr><td colSpan={6} className="text-center py-12 text-slate-500">{t("loading")}</td></tr>
                ) : filteredResellers.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-12 text-slate-500">{t("noResellers")}</td></tr>
                ) : (
                  filteredResellers.map((r, i) => {
                    const status = statusConfig[r.status] || statusConfig.pending;
                    const StatusIcon = status.icon;
                    return (
                      <motion.tr
                        key={r.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: i * 0.02 }}
                        className="hover:bg-slate-50"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                              <Users className="h-5 w-5 text-purple-600" />
                            </div>
                            <div>
                              <p className="font-medium">{r.name}</p>
                              <p className="text-sm text-slate-500">{r.user_email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <Badge variant="outline">{r.code}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={`${status.color} gap-1 border-0`}>
                            <StatusIcon className="h-3 w-3" />
                            <span className="hidden sm:inline">{r.status}</span>
                          </Badge>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <div className="flex items-center gap-1">
                            <Building2 className="h-4 w-4 text-slate-400" />
                            <span>{Math.floor(Math.random() * 10) + 1}</span>
                            <span className="text-slate-400">/ {r.max_merchants || 10}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-500 hidden lg:table-cell">
                          {r.created_date ? format(new Date(r.created_date), "MMM d, yyyy") : "N/A"}
                        </td>
                        <td className="px-4 py-3">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem><Eye className="h-4 w-4 mr-2" />{t("view")}</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleEditReseller(r)}><Pencil className="h-4 w-4 mr-2" />{t("edit")}</DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => setStatusDialog(r)}>
                                {r.status === "active" ? (
                                  <><PowerOff className="h-4 w-4 mr-2" />{t("suspend")}</>
                                ) : (
                                  <><Power className="h-4 w-4 mr-2" />{t("activate")}</>
                                )}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </motion.tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <AlertDialog open={!!statusDialog} onOpenChange={() => setStatusDialog(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {statusDialog?.status === "active" ? t("suspendReseller") : t("activateReseller")}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {statusDialog?.status === "active"
                  ? `${t("suspendMerchantConfirm")} "${statusDialog?.name}"?`
                  : `${t("activateMerchantConfirm")} "${statusDialog?.name}"?`
                }
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleToggleStatus}
                className={statusDialog?.status === "active" ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700"}
              >
                {statusDialog?.status === "active" ? t("suspend") : t("activate")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <EngineResellerForm
          open={showForm}
          onClose={() => { setShowForm(false); setEditingReseller(null); }}
          reseller={editingReseller}
          onSave={handleSaveReseller}
          saving={saving}
        />
      </div>
    </EngineLayout>
  );
}